import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { useForm, Controller } from "react-hook-form";
import { useReportsStore } from "../../../store/reports/reportsStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAlert } from "../../../context/AlertContext";
import InfiniteDeviceSelect, { useReportDeviceIds } from "../../../components/InfiniteDeviceSelect";
import { allDevicesQueryOptions } from "../../../features/devices/queries";
import { useReportCompanyScope } from "../hooks/useReportCompanyScope";
import { useReportTemperatureStore } from "../../../store/reports/reportTemperatureStore";
import ReportPointsTable, { type ReportTableColumn } from "../components/ReportPointsTable";
import { ReportStatCard, ReportChart, tickColor, gridColor, reportLegend } from "../components/ReportCharts";
import ReportLoadingPanel from "../components/ReportLoadingPanel";
import { toLocalInputValue, makeDateTimeFormatter, resolveTz } from "../utils/reportDateFormat";
import { exportHistoricToExcel, exportHistoricToPdf } from "../utils/exportHistoric";
import type { ChartConfiguration } from "chart.js";
import ReportActionButton from "../components/ReportActionButton";
import ReportDateRangePicker from "../components/ReportDateRangePicker";
import ReportFormatToggle from "../components/ReportFormatToggle";
import ReportMinMaxInput from "../components/ReportMinMaxInput";
import ReportTemperatureScaleToggle from "../components/ReportTemperatureScaleToggle";

const MAX_RANGE_DAYS = 31;
const MAX_CHART_POINTS = 300;
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

interface TemperatureFormData {
  devices: string[];
  startDate: string;
  endDate: string;
  typeReport: string;
  typeDocument: "excel" | "pdf";
  tempScale: "celsius" | "fahrenheit";
  highLimit: string;
  lowLimit: string;
}

interface TempRow {
  id: string;
  unidad: string;
  fecha: string;
  ignicion: string;
  velocidad: string;
  temperatura: string;
  estado: string;
}

const TEMP_COLUMNS: ReportTableColumn<TempRow>[] = [
  { key: "unidad", header: "Unidad" },
  { key: "fecha", header: "Fecha" },
  { key: "ignicion", header: "Ignición" },
  { key: "velocidad", header: "Velocidad", align: "right" },
  { key: "temperatura", header: "Temperatura", align: "right" },
  { key: "estado", header: "Estado" },
];

const downsample = <T,>(arr: T[], max: number): T[] => {
  if (arr.length <= max) return arr;
  const step = Math.ceil(arr.length / max);
  return arr.filter((_, i) => i % step === 0);
};

export default function TemperatureSection() {
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const generateHistoryReport = useReportsStore((state) => state.generateHistoryReport);
  const loading = useReportsStore((state) => state.loading);
  const { loadedDeviceIds, loadedCount, totalDevices } = useReportDeviceIds();

  const loadRange = useReportTemperatureStore((state) => state.loadRange);
  const clearTemp = useReportTemperatureStore((state) => state.clear);
  const resultsState = useReportTemperatureStore((state) => state.results);
  const order = useReportTemperatureStore((state) => state.order);
  const tempLoading = useReportTemperatureStore((state) => state.loading);
  const tz = useReportTemperatureStore((state) => state.tz);
  const scale = useReportTemperatureStore((state) => state.scale);
  const highLimit = useReportTemperatureStore((state) => state.highLimit);
  const lowLimit = useReportTemperatureStore((state) => state.lowLimit);

  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [loadAttempted, setLoadAttempted] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm<TemperatureFormData>({
    mode: "onBlur",
    defaultValues: {
      devices: [],
      startDate: "",
      endDate: "",
      typeReport: "temperature",
      typeDocument: "pdf",
      tempScale: "celsius",
      highLimit: "",
      lowLimit: "",
    },
  });

  const { showAlert } = useAlert();
  const { companyId, disableWithoutCompany } = useReportCompanyScope(setValue, () => {
    clearTemp();
    setLoadAttempted(false);
  });
  const { data: allDevices } = useQuery({ ...allDevicesQueryOptions(companyId) });

  const selectedDevices = watch("devices");
  const startDate = watch("startDate");
  const endDate = watch("endDate");

  const hasDevices = (selectedDevices?.length ?? 0) > 0;
  const hasDates = Boolean(startDate && endDate);
  const rangeDays = useMemo(() => {
    if (!hasDates) return 0;
    return (new Date(endDate).getTime() - new Date(startDate).getTime()) / DAY_MS;
  }, [endDate, hasDates, startDate]);
  const hasValidRange = hasDates && rangeDays >= 0;
  const isWithinViewLimit = hasValidRange && rangeDays <= MAX_RANGE_DAYS;
  const filtersReadyForView = hasDevices && hasValidRange && isWithinViewLimit;

  const scaleSymbol = scale === "fahrenheit" ? "°F" : "°C";

  const handleSelectAll = () => {
    if (selectedDevices?.length === loadedDeviceIds.length) {
      setValue("devices", []);
    } else {
      setValue("devices", loadedDeviceIds);
    }
  };

  const onSubmit = async (formData: TemperatureFormData) => {
    const result = await generateHistoryReport(
      {
        deviceIds: formData.devices,
        startDate: formData.startDate,
        endDate: formData.endDate,
        typeReport: formData.typeReport,
        typeDocument: formData.typeDocument,
        tempScale: formData.tempScale,
        highLimit: formData.highLimit,
        lowLimit: formData.lowLimit,
      },
      showAlert,
      () => {},
    );

    if (result.success && result.data) {
      const extension = formData.typeDocument === "pdf" ? "pdf" : "xlsx";
      const blob = new Blob([result.data], {
        type:
          formData.typeDocument === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.fileName || `reporte.${extension}`;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const resolveNameById = useCallback((): Record<string, string> => {
    const map: Record<string, string> = {};
    (allDevices ?? []).forEach((d: any) => {
      map[d.id] = d.vehicle?.name || d.identifier || d.id;
    });
    return map;
  }, [allDevices]);

  const onViewTemp = async () => {
    const { devices, startDate, endDate, tempScale, highLimit, lowLimit } = getValues();

    if (!devices || devices.length === 0) {
      showAlert({
        title: "Selecciona unidades",
        text: "Selecciona al menos un equipo para ver la temperatura.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    if (!startDate || !endDate) {
      showAlert({
        title: "Fechas requeridas",
        text: "Selecciona la fecha de inicio y fin.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    const days = (new Date(endDate).getTime() - new Date(startDate).getTime()) / DAY_MS;
    if (days > MAX_RANGE_DAYS) {
      showAlert({
        title: "Rango demasiado amplio",
        text: `Ver temperatura solo muestra hasta ${MAX_RANGE_DAYS} días. Para rangos mayores usa "Generar Reporte".`,
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    const fromIso = new Date(startDate).toISOString();
    const toIso = new Date(endDate).toISOString();
    const nameById = resolveNameById();
    const devicesToLoad = devices.map((id) => ({
      deviceId: id,
      vehicleName: nameById[id] || id,
    }));

    await loadRange(devicesToLoad, fromIso, toIso, {
      scale: (tempScale as "celsius" | "fahrenheit") || "celsius",
      highLimit: parseFloat(highLimit) || 0,
      lowLimit: parseFloat(lowLimit) || 0,
    });
    setLoadAttempted(true);
  };

  const orderedResults = useMemo(
    () => order.map((id) => resultsState[id]).filter(Boolean),
    [order, resultsState],
  );

  const totals = useMemo(() => {
    let min: number | null = null;
    let max: number | null = null;
    let sum = 0;
    let count = 0;
    let breaches = 0;
    orderedResults.forEach((r) => {
      r.points.forEach((p) => {
        sum += p.temp;
        count++;
        if (p.breach) breaches++;
        min = min == null ? p.temp : Math.min(min, p.temp);
        max = max == null ? p.temp : Math.max(max, p.temp);
      });
    });
    return { min, max, avg: count > 0 ? sum / count : null, breaches, count };
  }, [orderedResults]);

  const fmtDateTime = useMemo(() => makeDateTimeFormatter(resolveTz(tz), false), [tz]);

  const tableRows = useMemo<TempRow[]>(
    () =>
      orderedResults.flatMap((r) =>
        r.points.map((p, i) => ({
          id: `${r.deviceId}-${p.ts}-${i}`,
          unidad: r.vehicleName,
          fecha: fmtDateTime(p.ts),
          ignicion: p.ignition ? "encendido" : "apagado",
          velocidad: `${p.speed.toFixed(0)} km/h`,
          temperatura: `${p.temp.toFixed(1)} ${scaleSymbol}`,
          estado: p.breach ? "Fuera de rango" : "Dentro de rango",
        })),
      ),
    [orderedResults, fmtDateTime, scaleSymbol],
  );

  const chartConfig = useMemo<ChartConfiguration | null>(() => {
    const withData = orderedResults.filter((r) => r.points.length > 0);
    if (withData.length === 0) return null;

    let minTs = Infinity;
    let maxTs = -Infinity;
    withData.forEach((r) => {
      r.points.forEach((p) => {
        if (p.ts < minTs) minTs = p.ts;
        if (p.ts > maxTs) maxTs = p.ts;
      });
    });

    const span = maxTs - minTs;
    const axisFormatter = new Intl.DateTimeFormat("es-ES", {
      timeZone: resolveTz(tz),
      day: "2-digit",
      month: "2-digit",
      ...(span > 3 * DAY_MS ? {} : { hour: "2-digit", minute: "2-digit", hour12: false }),
    });

    const datasets: any[] = withData.map((r) => {
      const sampled = downsample(r.points, MAX_CHART_POINTS);
      return {
        label: r.vehicleName,
        data: sampled.map((p) => ({ x: p.ts, y: Number(p.temp.toFixed(1)) })),
        borderColor: r.color,
        backgroundColor: r.color,
        borderWidth: 2.25,
        pointRadius: 0,
        tension: 0.25,
      };
    });

    datasets.push({
      label: `Límite alto (${highLimit.toFixed(1)} ${scaleSymbol})`,
      data: [
        { x: minTs, y: highLimit },
        { x: maxTs, y: highLimit },
      ],
      borderColor: "#DC2626",
      borderWidth: 1.75,
      borderDash: [6, 4],
      pointRadius: 0,
    });
    datasets.push({
      label: `Límite bajo (${lowLimit.toFixed(1)} ${scaleSymbol})`,
      data: [
        { x: minTs, y: lowLimit },
        { x: maxTs, y: lowLimit },
      ],
      borderColor: "#2563EB",
      borderWidth: 1.75,
      borderDash: [6, 4],
      pointRadius: 0,
    });

    return {
      type: "line",
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "nearest", intersect: false },
        plugins: {
          legend: reportLegend(isDarkMode),
          tooltip: {
            callbacks: {
              title: (items: any[]) => fmtDateTime(Number(items[0].parsed.x)),
              label: (item: any) => `${item.dataset.label}: ${item.parsed.y} ${scaleSymbol}`,
            },
          },
        },
        scales: {
          x: {
            type: "linear",
            ticks: {
              color: tickColor(isDarkMode),
              callback: (value) => axisFormatter.format(new Date(Number(value))),
              maxTicksLimit: 10,
              maxRotation: 0,
              autoSkip: true,
            },
            grid: { color: gridColor(isDarkMode) },
          },
          y: {
            title: { display: true, text: scaleSymbol, color: tickColor(isDarkMode) },
            ticks: { color: tickColor(isDarkMode) },
            grid: { color: gridColor(isDarkMode) },
          },
        },
      },
    };
  }, [orderedResults, isDarkMode, tz, highLimit, lowLimit, scaleSymbol, fmtDateTime]);

  const onDownload = async (typeDocument: "pdf" | "excel") => {
    if (tableRows.length === 0) return;
    const { startDate, endDate } = getValues();
    const extension = typeDocument === "pdf" ? "pdf" : "xlsx";
    const fileName = `temperature_${startDate.split("T")[0]}_${endDate.split("T")[0]}.${extension}`;
    const period =
      `Período: ${startDate.replace("T", " ")} al ${endDate.replace("T", " ")}` +
      ` | Fuera de rango: ${totals.breaches}`;

    setExporting(typeDocument);
    await new Promise((r) => setTimeout(r, 50));
    try {
      const meta = { title: "Informe de Temperatura", period, fileName };
      if (typeDocument === "excel") {
        await exportHistoricToExcel(tableRows, TEMP_COLUMNS, meta);
      } else {
        exportHistoricToPdf(tableRows, TEMP_COLUMNS, meta);
      }
    } catch {
      showAlert({
        title: "Error al descargar",
        text: "No se pudo generar el archivo de temperatura.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    setValue("startDate", toLocalInputValue(new Date()));
    setValue("endDate", toLocalInputValue(new Date(new Date().setDate(new Date().getDate() + 15))));
  }, []);

  useEffect(() => {
    return () => {
      clearTemp();
      setLoadAttempted(false);
    };
  }, [clearTemp]);

  const hasResults = orderedResults.length > 0 && totals.count > 0;
  const showSkeleton = tempLoading;
  const showEmptyState = !hasResults && !showSkeleton;

  const fmtTemp = (n: number | null): string => (n == null ? "N/A" : `${n.toFixed(1)} ${scaleSymbol}`);

  const summaryCards = [
    { label: "Temp. mínima", value: fmtTemp(totals.min), icon: "ri-temp-cold-line" },
    { label: "Temp. máxima", value: fmtTemp(totals.max), icon: "ri-temp-hot-line" },
    { label: "Temp. promedio", value: fmtTemp(totals.avg), icon: "ri-dashboard-3-line" },
    { label: "Fuera de rango", value: String(totals.breaches), icon: "ri-alarm-warning-line" },
  ];

  return (
    <div className="h-full w-full overflow-y-auto p-2 sm:p-4">
      <form onSubmit={handleSubmit(onSubmit)} className={`flex flex-col lg:flex-row ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-3.5 gap-3 justify-between lg:items-end`}>
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4 w-full lg:w-auto">
          <div className="flex flex-col gap-1 min-w-52 w-full lg:w-auto">
            <div className="flex justify-between items-center">
              <label htmlFor="devices" className={`${themeClasses.text} text-xs`}>
                Equipos {selectedDevices?.length > 0 && `(${selectedDevices.length}/${totalDevices})`}
              </label>
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={disableWithoutCompany}
                className={`text-xs px-2 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} hover:opacity-90 disabled:opacity-50`}
              >
                {selectedDevices?.length === loadedDeviceIds.length && loadedCount > 0 ? "Deseleccionar todos" : `Seleccionar todos (${loadedCount})`}
              </button>
            </div>
            <div>
              <Controller
                name="devices"
                control={control}
                rules={{ required: "Selecciona al menos un equipo" }}
                render={({ field }) => (
                  <InfiniteDeviceSelect
                    value={field.value}
                    onChange={field.onChange}
                    isDarkMode={isDarkMode}
                    themeClasses={themeClasses}
                  />
                )}
              />
              {errors.devices && <span className="text-red-500 text-sm">{errors.devices.message}</span>}
            </div>
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label className={`${themeClasses.text} text-xs`}>Rango de fechas</label>
            <input type="hidden" {...register("startDate", { required: "Fecha inicio requerida" })} />
            <input type="hidden" {...register("endDate", { required: "Fecha fin requerida" })} />
            <ReportDateRangePicker
              startValue={watch("startDate")}
              endValue={watch("endDate")}
              onChangeStart={(v) => setValue("startDate", v, { shouldValidate: true, shouldDirty: true })}
              onChangeEnd={(v) => setValue("endDate", v, { shouldValidate: true, shouldDirty: true })}
              startError={errors.startDate?.message}
              endError={errors.endDate?.message}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={`${themeClasses.text} text-xs`}>Escala</label>
            <Controller
              name="tempScale"
              control={control}
              render={({ field }) => (
                <ReportTemperatureScaleToggle value={field.value} onChange={field.onChange} />
              )}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={`${themeClasses.text} text-xs`}>Límites (bajo–alto)</label>
            <ReportMinMaxInput
              lowValue={watch("lowLimit")}
              highValue={watch("highLimit")}
              onChangeLow={(v) => setValue("lowLimit", v)}
              onChangeHigh={(v) => setValue("highLimit", v)}
              lowPlaceholder="0"
              highPlaceholder="100"
              unit={watch("tempScale") === "fahrenheit" ? "°F" : "°C"}
            />
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label className={`${themeClasses.text} text-xs`}>Formato</label>
            <Controller
              name="typeDocument"
              control={control}
              render={({ field }) => <ReportFormatToggle value={field.value} onChange={field.onChange} />}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 w-full lg:w-auto">
          <ReportActionButton
            type="button"
            variant="secondary"
            onClick={onViewTemp}
            disabled={tempLoading || !filtersReadyForView || disableWithoutCompany}
            title={!filtersReadyForView && hasDevices && hasValidRange && !isWithinViewLimit ? `Ver temperatura solo admite hasta ${MAX_RANGE_DAYS} días` : undefined}
          >
            Ver temperatura
          </ReportActionButton>
          <ReportActionButton
            type="submit"
            disabled={loading || !hasDevices || !hasValidRange || disableWithoutCompany}
          >
            {loading ? "Generando..." : "Generar Reporte"}
          </ReportActionButton>
        </div>
      </form>

      {showSkeleton && (
        <ReportLoadingPanel
          isDarkMode={isDarkMode}
          icon="ri-temp-hot-line"
          title="Obteniendo temperatura…"
          subtitle="Leyendo el sensor del rango seleccionado"
        />
      )}

      {hasResults && !showSkeleton && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className={`text-sm ${themeClasses.text}`}>
              {orderedResults.length} unidad(es) · {totals.count} lectura(s)
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => onDownload("pdf")}
                disabled={exporting !== null || tableRows.length === 0}
                className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
              >
                {exporting === "pdf" ? "Generando..." : "Descargar PDF"}
              </button>
              <button
                type="button"
                onClick={() => onDownload("excel")}
                disabled={exporting !== null || tableRows.length === 0}
                className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
              >
                {exporting === "excel" ? "Generando..." : "Descargar Excel"}
              </button>
              <button
                type="button"
                onClick={() => {
                  clearTemp();
                  setLoadAttempted(false);
                }}
                className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2}`}
              >
                Cerrar
              </button>
            </div>
          </div>

          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            {summaryCards.map((card) => (
              <ReportStatCard key={card.label} icon={card.icon} label={card.label} value={card.value} isDarkMode={isDarkMode} />
            ))}
          </div>

          {chartConfig && (
            <div className={`sticky top-0 z-20 p-3 rounded-xl border shadow-sm ${isDarkMode ? "bg-dark-card border-dark-border" : "bg-gray-50 border-gray-200"}`}>
              <p className={`text-sm font-semibold mb-2 ${themeClasses.text}`}>Temperatura en el tiempo</p>
              <ReportChart config={chartConfig} height={300} />
            </div>
          )}

          <ReportPointsTable
            data={tableRows}
            columns={TEMP_COLUMNS}
            isDarkMode={isDarkMode}
            emptyText="No se encontró temperatura en el rango seleccionado."
          />
        </div>
      )}

      {showEmptyState && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className={`relative w-full overflow-hidden rounded-xl border border-dashed ${themeClasses.cardBorder} ${isDarkMode ? "bg-white/5" : "bg-black/[0.02]"}`} style={{ minHeight: 220 }}>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
              <span className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${isDarkMode ? "bg-primary-500/15" : "bg-primary-500/10"}`}>
                <i className="ri-temp-hot-line text-primary-500 text-2xl" />
              </span>
              <div>
                <p className={`text-sm font-semibold ${themeClasses.text}`}>Temperatura de la caja</p>
                <p className={`text-xs mt-1 max-w-md ${themeClasses.textSecondary}`}>
                  Configura límites, equipos y fechas, y usa <strong>Ver temperatura</strong> (hasta {MAX_RANGE_DAYS} días) para ver la curva con los límites. Si las unidades no reportan temperatura no habrá datos; usa <strong>Generar Reporte</strong> para rangos mayores.
                </p>
              </div>
            </div>
          </div>

          {loadAttempted && !tempLoading && (
            <div className={`p-3 rounded-xl border ${isDarkMode ? "bg-dark-card border-dark-border" : "bg-gray-100 border-gray-200"}`}>
              <div className="flex items-start space-x-2">
                <i className={`ri-information-line text-base ${themeClasses.textSecondary}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${themeClasses.textSecondary}`}>Sin resultados</p>
                  <p className={`text-sm ${themeClasses.text} mt-0.5`}>
                    No se encontró temperatura en el rango seleccionado. Es posible que las unidades no tengan sensor de temperatura. Verifica equipos y fechas, o usa <strong>Generar Reporte</strong>.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
